import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useAdminService } from '../services/admin.service';

interface AdminContextType {
  isAdmin: boolean;
  isLoading: boolean;
  checkAdminStatus: () => Promise<boolean>;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: false,
  isLoading: true,
  checkAdminStatus: async () => false
});

export const useAdmin = () => {
  return useContext(AdminContext);
};

export const AdminProvider = ({ children }: { children: ReactNode }) => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { currentUser } = useAuth();
  const { checkIsAdmin } = useAdminService();

  const checkAdminStatus = async (): Promise<boolean> => {
    if (!currentUser) {
      setIsAdmin(false);
      return false;
    }

    try {
      const { isAdmin } = await checkIsAdmin();
      setIsAdmin(isAdmin);
      return isAdmin;
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const verifyAdminStatus = async () => {
      setIsLoading(true);
      await checkAdminStatus();
    };

    if (currentUser) {
      verifyAdminStatus();
    } else {
      setIsAdmin(false);
      setIsLoading(false);
    }
  }, [currentUser]);

  return (
    <AdminContext.Provider value={{ isAdmin, isLoading, checkAdminStatus }}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
